import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, assetUrl } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ReelInsights } from "../components/ReelInsights";
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { FileText, Clock, CheckCircle2, MessageCircle, ShieldAlert, Sparkles, ArrowRight, Mail, Send, Heart, Play, Eye, Film, MousePointerClick, CalendarClock, TrendingUp } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, tid }) => (
  <Card className="hover:-translate-y-1 hover:shadow-lg transition-transform duration-200" data-testid={tid}>
    <CardContent className="p-6 flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</p>
        <p className="font-heading text-3xl font-semibold mt-2">{value}</p>
      </div>
      <div className="p-2.5 rounded-lg bg-secondary text-secondary-foreground"><Icon size={18} /></div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { selected, brands } = useBrand();
  const activeBrand = brands.find((b) => b.name === selected);
  const [stats, setStats] = useState(null);
  const [heat, setHeat] = useState(null);
  const [topPosts, setTopPosts] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [queue, setQueue] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]);
  const [healthNudges, setHealthNudges] = useState([]);
  const [healthTrend, setHealthTrend] = useState({});
  const [healthDeltas, setHealthDeltas] = useState({});

  useEffect(() => {
    api.get("/assets/recent", { params: { limit: 6 } }).then((r) => setRecentUploads(r.data || [])).catch(() => setRecentUploads([]));
    api.get("/kb/health-nudges", { params: { threshold: 60 } }).then(async (r) => {
      const nudges = r.data || [];
      setHealthNudges(nudges);
      // Fetch upload delta for each brand-with-nudge (up to 3 shown) so the
      // "+N pts from your last upload" chip appears alongside the sparkline.
      const targets = nudges.slice(0, 3);
      const results = await Promise.all(targets.map((n) =>
        api.get("/brands/health/last-upload-delta", { params: n.brand ? { brand: n.brand } : {} })
          .then((res) => [n.brand || "", res.data])
          .catch(() => [n.brand || "", null])));
      const map = {};
      results.forEach(([k, v]) => { if (v && v.delta > 0) map[k] = v; });
      setHealthDeltas(map);
    }).catch(() => setHealthNudges([]));
    api.get("/brands/health/trend", { params: { days: 30 } }).then((r) => setHealthTrend(r.data || {})).catch(() => setHealthTrend({}));
  }, [selected]);

  useEffect(() => {
    setStats(null);
    api.get("/dashboard/stats", { params: selected ? { brand: selected } : {} }).then((r) => setStats(r.data));
    api.get("/analytics/heatmap").then((r) => setHeat(r.data));
    api.get("/analytics/top-posts", { params: selected ? { brand: selected } : {} }).then((r) => setTopPosts(r.data));
    api.get("/analytics/click-ranking", { params: selected ? { brand: selected } : {} }).then((r) => setFunnel(r.data)).catch(() => {});
    api.get("/analytics/publish-queue", { params: selected ? { brand: selected } : {} }).then((r) => setQueue(r.data)).catch(() => setQueue({ posts: [] }));
    api.get("/digest/latest").then((r) => setDigest(r.data?.id ? r.data : null));
  }, [selected]);

  const genDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/digest/generate");
      setDigest(data);
      toast.success(`Digest emailed to ${data.recipient}${data.email_sent ? "" : " (simulated — add a Resend key to send for real)"}`);
    } finally {
      setDigestBusy(false);
    }
  };

  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const openPreview = async () => {
    setPreviewBusy(true);
    try {
      const { data } = await api.get("/digest/preview");
      setPreview(data);
    } finally {
      setPreviewBusy(false);
    }
  };

  const [tokenHealth, setTokenHealth] = useState(null);
  useEffect(() => {
    api.get("/integrations/token-health").then((r) => setTokenHealth(r.data)).catch(() => {});
  }, []);

  const [recap, setRecap] = useState(null);
  const [recapOffset, setRecapOffset] = useState(0); // 0 = this week to date, 1 = last week
  const [recapBusy, setRecapBusy] = useState(false);
  const loadRecap = (offset = recapOffset) => {
    setRecapBusy(true);
    setRecap(null);
    api.get("/analytics/weekly-recap", { params: { ...(selected ? { brand: selected } : {}), week_offset: offset } })
      .then((r) => setRecap(r.data))
      .catch(() => setRecap({ ai_summary: "Could not build a recap right now." }))
      .finally(() => setRecapBusy(false));
  };
  useEffect(() => { loadRecap(0); setRecapOffset(0); /* eslint-disable-next-line */ }, [selected]);

  if (!stats) return <p className="text-muted-foreground">Loading...</p>;
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const badTokens = (tokenHealth?.pages || []).filter((p) => p.status !== "ok");

  return (
    <div className="space-y-8 fade-up" data-testid="dashboard-page">
      {badTokens.length > 0 && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-5 py-4" data-testid="token-health-banner">
          <p className="text-sm font-semibold flex items-center gap-2"><ShieldAlert size={16} className="text-destructive" /> Meta connection needs attention</p>
          <ul className="text-sm text-muted-foreground mt-1 space-y-0.5">
            {badTokens.map((p) => (
              <li key={p.name} data-testid={`token-warning-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <span className="font-medium text-foreground">{p.name}</span>: {p.status === "expired" ? "token has expired — publishing for this page is broken" : `token expires in ${p.days_left} days`}
              </li>
            ))}
          </ul>
          <Link to="/settings" className="text-sm font-semibold text-destructive hover:underline inline-flex items-center gap-1 mt-2">Reconnect in Settings <ArrowRight size={12} /></Link>
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-4">
          {selected && activeBrand?.logo_url && (
            <img src={assetUrl(activeBrand.logo_url)} alt={`${selected} logo`} data-testid="brand-dashboard-logo" className="w-14 h-14 rounded-full object-cover border-2 shrink-0" style={{ borderColor: activeBrand.color || "transparent" }} />
          )}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-1">{selected ? "Brand workspace" : "HWG Overview"}</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold">{selected || "Harmony Wellness Group"}</h1>
            {selected ? (
              <p className="text-muted-foreground mt-1">Dedicated social accounts for this brand <span className="text-accent font-semibold">(simulated)</span></p>
            ) : stats.brand ? (
              <p className="text-muted-foreground mt-1">Group overview across <span className="font-semibold text-foreground">all brands</span></p>
            ) : (
              <p className="text-muted-foreground mt-1">Start by ingesting your brand website.</p>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          {!stats.brand && <Button asChild variant="outline" data-testid="setup-brand-btn"><Link to="/brand">Set up brand <ArrowRight size={14} className="ml-2" /></Link></Button>}
          <Button asChild data-testid="new-campaign-btn"><Link to="/generator"><Sparkles size={14} className="mr-2" />New campaign</Link></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Total posts" value={stats.total_posts} tid="stat-total-posts" />
        <StatCard icon={Clock} label="Pending review" value={stats.pending_review} tid="stat-pending" />
        <StatCard icon={CheckCircle2} label="Approved / scheduled" value={stats.approved} tid="stat-approved" />
        <StatCard icon={Send} label="Published" value={stats.published || 0} tid="stat-published" />
      </div>

      {healthNudges.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dashboard-kb-health-nudges">
          {healthNudges.slice(0, 3).map((n) => {
            const series = healthTrend[n.name] || [];
            const delta = healthDeltas[n.brand || ""];
            const slug = (n.brand || "umbrella").replace(/\s+/g, "-").toLowerCase();
            return (
              <Card key={n.brand || "umbrella"} className="border-amber-500/40 bg-amber-500/5" data-testid={`kb-health-nudge-${slug}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs uppercase tracking-widest font-semibold text-amber-600">Knowledge low · {n.score}/100</p>
                    <Badge variant="outline" className="text-[10px] shrink-0">{n.counts.pdfs} PDFs</Badge>
                  </div>
                  <p className="font-heading text-lg font-semibold truncate" title={n.name}>{n.name}</p>
                  {delta && (
                    <div
                      data-testid={`kb-health-nudge-delta-${slug}`}
                      title={delta.filename || ""}
                      className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-accent/15 text-accent"
                    >
                      <TrendingUp size={11} /> +{delta.delta} pts from your last upload
                    </div>
                  )}
                  {series.length > 1 && (
                    <div className="h-8 mt-1 -mx-1 min-w-[1px]" data-testid={`kb-health-sparkline-${slug}`}>
                      <ResponsiveContainer width="99%" height="100%">
                        <LineChart data={series} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                          <Line type="monotone" dataKey="score" stroke="hsl(var(--accent))" strokeWidth={2} dot={false} isAnimationActive={false} />
                          <YAxis hide domain={[0, 100]} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{n.top_tip}</p>
                  <Button asChild size="sm" variant="outline" className="mt-3 w-full" data-testid={`kb-health-nudge-cta-${slug}`}>
                    <Link to="/brand">Upload a PDF <ArrowRight size={12} className="ml-1.5" /></Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {recentUploads.length > 0 && (
        <Card data-testid="dashboard-recent-uploads">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-xl flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><FileText size={18} /> Recently added to your knowledge base</span>
              <Button asChild variant="ghost" size="sm"><Link to="/brand">Manage <ArrowRight size={12} className="ml-1.5" /></Link></Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recentUploads.map((u) => (
                <div key={u.id} data-testid={`dashboard-upload-${u.id}`} className="p-3 rounded-lg bg-muted flex items-start gap-2">
                  <FileText size={14} className="text-accent shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate" title={u.filename}>{u.filename}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[11px] text-muted-foreground">{u.brand || "HWG"} · {new Date(u.created_at).toLocaleDateString()}</span>
                      {u.status === "failed" && <Badge variant="destructive" className="text-[10px]">Failed</Badge>}
                      {u.status === "processing" && <Badge variant="outline" className="text-[10px]">Processing…</Badge>}
                      {u.ocr_used && <Badge variant="secondary" className="text-[10px]">OCR</Badge>}
                      {u.status === "ready" && (
                        <Badge variant="outline" className="text-[10px]" data-testid={`dashboard-upload-posts-${u.id}`}>
                          {u.post_count} post{u.post_count === 1 ? "" : "s"}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="weekly-recap-card" className="border-accent/40">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkles size={18} className="text-accent" /> Weekly recap
              {recap && <Badge variant="secondary" className="font-sans font-normal text-[10px]">{recap.week_start} → {recap.week_end}</Badge>}
              {selected && <Badge className="font-sans font-normal text-[10px]">{selected}</Badge>}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">How this feed performed — best pillar, best companion, one suggestion for next week.</p>
          </div>
          <div className="flex gap-1">
            {[{ v: 0, l: "This week" }, { v: 1, l: "Last week" }, { v: 2, l: "2 wks ago" }].map((o) => (
              <button key={o.v} type="button" data-testid={`recap-week-${o.v}`}
                onClick={() => { setRecapOffset(o.v); loadRecap(o.v); }}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${recapOffset === o.v ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {recapBusy && <p className="text-sm text-muted-foreground">Building recap…</p>}
          {!recapBusy && recap && (
            <div className="space-y-4">
              {recap.ai_summary ? (
                <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="recap-ai-summary">{recap.ai_summary}</p>
              ) : (
                <p className="text-sm text-muted-foreground">Nothing shipped in this window yet. Fire a batch to get started.</p>
              )}
              {recap.total_posts > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5 font-semibold uppercase tracking-wide">Top pillars</p>
                    <ul className="text-sm space-y-1" data-testid="recap-top-pillars">
                      {(recap.top_pillars || []).slice(0, 3).map((r) => (
                        <li key={r.name} className="flex justify-between gap-2">
                          <span className="truncate">{r.name}</span>
                          <span className="text-muted-foreground text-xs shrink-0">{r.posts}p · {r.score}pts</span>
                        </li>
                      ))}
                      {!recap.top_pillars?.length && <li className="text-xs text-muted-foreground">—</li>}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5 font-semibold uppercase tracking-wide">Top companions</p>
                    <ul className="text-sm space-y-1" data-testid="recap-top-companions">
                      {(recap.top_companions || []).slice(0, 3).map((r) => (
                        <li key={r.name} className="flex justify-between gap-2">
                          <span className="truncate capitalize">{r.name}</span>
                          <span className="text-muted-foreground text-xs shrink-0">{r.posts}p · {r.score}pts</span>
                        </li>
                      ))}
                      {!recap.top_companions?.length && <li className="text-xs text-muted-foreground">No companion posts yet</li>}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5 font-semibold uppercase tracking-wide">Top posts</p>
                    <ul className="text-sm space-y-1" data-testid="recap-top-posts">
                      {(recap.top_posts || []).slice(0, 3).map((p) => (
                        <li key={p.id} className="flex justify-between gap-2">
                          <span className="truncate">{p.title}</span>
                          <span className="text-muted-foreground text-xs shrink-0">{p.score}pts</span>
                        </li>
                      ))}
                      {!recap.top_posts?.length && <li className="text-xs text-muted-foreground">—</li>}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {queue && (
        <Card data-testid="publish-queue-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <CalendarClock size={18} className="text-accent" /> Going live tomorrow
              <Badge variant="secondary" className="font-sans font-normal text-[10px]">
                {queue.posts.length} post{queue.posts.length === 1 ? "" : "s"}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {queue.posts.length === 0 ? (
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted text-sm text-muted-foreground">
                <span>Nothing scheduled for tomorrow yet.</span>
                <Button asChild size="sm" variant="outline" data-testid="queue-new-btn">
                  <Link to="/generator"><Sparkles size={13} className="mr-1.5" /> Plan a batch</Link>
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {queue.posts.map((p) => {
                  const when = new Date(p.scheduled_at);
                  const time = when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZone: queue.tz });
                  return (
                    <Link key={p.id} to={p.batch_id ? `/review?batch=${p.batch_id}` : "/review"}
                      className="rounded-xl border border-border bg-muted/40 overflow-hidden flex gap-3 items-stretch hover:border-accent transition-colors"
                      data-testid={`queue-post-${p.id}`}>
                      {p.image_url ? (
                        <img src={assetUrl(p.image_url)} alt="" className="w-20 h-20 object-cover shrink-0" />
                      ) : (
                        <div className="w-20 h-20 bg-muted shrink-0 flex items-center justify-center"><Film size={18} className="text-muted-foreground" /></div>
                      )}
                      <div className="p-2.5 flex-1 min-w-0 flex flex-col justify-between">
                        <div>
                          <p className="text-sm font-semibold truncate">{p.title}</p>
                          <p className="text-[11px] text-muted-foreground capitalize truncate">{p.platform}{p.brand ? ` · ${p.brand}` : ""}{p.pillar ? ` · ${p.pillar}` : ""}</p>
                        </div>
                        <div className="flex items-center justify-between text-[11px] mt-1">
                          <span className="flex items-center gap-1 text-accent font-semibold"><Clock size={11} /> {time}</span>
                          <Badge variant={p.review_status === "scheduled" || p.review_status === "approved" ? "default" : "outline"} className="text-[9px] capitalize">
                            {p.review_status === "pending_review" ? "needs review" : p.review_status}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!selected && stats.brand_breakdown?.length > 0 && (
        <Card data-testid="brand-breakdown-card">
          <CardHeader><CardTitle className="font-heading text-xl">Brands at a glance</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stats.brand_breakdown.map((b) => (
                <div key={b.name} className="p-5 rounded-xl border border-border bg-muted/40 space-y-3" data-testid={`brand-glance-${b.name}`}>
                  <p className="font-heading font-semibold">{b.name}</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><p className="font-heading text-2xl font-semibold">{b.total}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Posts</p></div>
                    <div><p className="font-heading text-2xl font-semibold">{b.published}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Published</p></div>
                    <div><p className="font-heading text-2xl font-semibold">{b.pending}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Pending</p></div>
                    <div><p className="font-heading text-2xl font-semibold">{b.unreplied}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Awaiting reply</p></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-heading text-xl">Engagement · last 14 days</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <AreaChart data={stats.engagement_series}>
                <defs>
                  <linearGradient id="eg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 10, color: "hsl(var(--foreground))" }} />
                <Area type="monotone" dataKey="engagement" stroke="hsl(var(--chart-1))" fill="url(#eg)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card data-testid="inbox-summary-card">
            <CardHeader><CardTitle className="font-heading text-xl">Community</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Link to="/inbox" className="flex items-center justify-between p-3 rounded-lg bg-muted hover:bg-secondary transition-colors" data-testid="unreplied-link">
                <span className="flex items-center gap-2 text-sm font-medium"><MessageCircle size={16} /> Awaiting reply</span>
                <span className="font-heading text-xl font-semibold">{stats.unreplied_messages}</span>
              </Link>
              <Link to="/inbox" className="flex items-center justify-between p-3 rounded-lg bg-muted hover:bg-secondary transition-colors" data-testid="flagged-link">
                <span className="flex items-center gap-2 text-sm font-medium"><ShieldAlert size={16} /> Flagged / spam</span>
                <span className="font-heading text-xl font-semibold">{stats.flagged_messages}</span>
              </Link>
            </CardContent>
          </Card>
          {heat && (
            <Card data-testid="best-times-card">
              <CardHeader><CardTitle className="font-heading text-xl">Best times to post</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {heat.best_slots.slice(0, 4).map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{days[s.day]} · {String(s.hour).padStart(2, "0")}:00</span>
                    <span className="font-semibold text-accent">{s.engagement} eng.</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {!selected && (
      <Card data-testid="digest-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-xl flex items-center gap-2"><Mail size={18} /> Weekly digest</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="preview-digest-btn" disabled={previewBusy} onClick={openPreview}>
              <Eye size={13} className="mr-2" /> {previewBusy ? "Loading..." : "Preview email"}
            </Button>
            <Button variant="outline" size="sm" data-testid="generate-digest-btn" disabled={digestBusy} onClick={genDigest}>
              <Send size={13} className="mr-2" /> {digestBusy ? "Sending..." : "Send now"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!digest ? (
            <p className="text-sm text-muted-foreground">Every Monday a summary of scheduled posts, pending approvals and unanswered messages is emailed to ask@harmonywellnessgroup.com.au. Click "Preview email" to read the upcoming edition before it sends.</p>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">Week of {digest.week_of} · emailed to {digest.recipient} <span className="text-accent font-semibold">(simulated)</span></p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[["Scheduled next 7d", digest.scheduled_count], ["Pending review", digest.pending_count], ["Changes requested", digest.changes_requested_count], ["Awaiting reply", digest.unreplied_count], ["Published last week", digest.published_last_week]].map(([l, v]) => (
                  <div key={l} className="p-3 rounded-lg bg-muted text-center">
                    <p className="font-heading text-2xl font-semibold">{v}</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{l}</p>
                  </div>
                ))}
              </div>
              {digest.upcoming?.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Coming up</p>
                  {digest.upcoming.map((p, i) => (
                    <p key={i} className="text-sm flex justify-between gap-3"><span className="truncate">{p.title}</span><span className="text-muted-foreground shrink-0 capitalize">{p.platform} · {new Date(p.scheduled_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span></p>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Monday digest preview · week of {preview?.digest?.week_of}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Sends every Monday to <span className="font-semibold text-foreground">{preview?.recipient}</span>
            {preview?.email_live ? <span className="text-accent font-semibold"> · live email</span> : <span> · simulated until a Resend key is added</span>}
          </p>
          {preview?.html && (
            <iframe title="Digest email preview" srcDoc={preview.html} sandbox="" data-testid="digest-preview-frame"
              className="w-full h-[540px] rounded-xl border border-border bg-white" />
          )}
        </DialogContent>
      </Dialog>

      {heat && (
        <Card data-testid="heatmap-card">
          <CardHeader><CardTitle className="font-heading text-xl">Engagement heatmap
            {heat?.real_data_points > 0 && <Badge variant="secondary" className="ml-2 font-sans font-normal text-[10px]" data-testid="real-data-badge">blended with real engagement from {heat.real_data_points} live post{heat.real_data_points !== 1 ? "s" : ""}</Badge>}
          </CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <div className="min-w-[720px]">
              <div className="grid gap-[3px]" style={{ gridTemplateColumns: "44px repeat(24, 1fr)" }}>
                <div />
                {Array.from({ length: 24 }, (_, h) => <div key={h} className="text-[9px] text-muted-foreground text-center">{h}</div>)}
                {days.map((d, di) => (
                  [<div key={d} className="text-[10px] text-muted-foreground flex items-center">{d}</div>,
                  ...Array.from({ length: 24 }, (_, h) => {
                    const cell = heat.heatmap.find((c) => c.day === di && c.hour === h);
                    const v = cell ? cell.engagement : 0;
                    const op = Math.min(1, Math.max(0.06, (v - 10) / 130));
                    return <div key={`${di}-${h}`} title={`${d} ${h}:00 — ${v}`} className="h-5 rounded-[3px]" style={{ background: `hsl(var(--accent-2) / ${op})` }} />;
                  })]
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {topPosts.length > 0 && (
        <Card data-testid="top-posts-card">
          <CardHeader><CardTitle className="font-heading text-xl">Top performing posts <Badge variant="secondary" className="ml-2 font-sans font-normal text-[10px]">real engagement · refreshed daily</Badge></CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {topPosts.map((p, i) => (
                <div key={p.id} className="rounded-xl border border-border overflow-hidden bg-muted/40" data-testid={`top-post-${p.id}`}>
                  {p.image_url && <img src={assetUrl(p.image_url)} alt="" className="w-full aspect-[2/1] object-cover" />}
                  <div className="p-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-accent">#{i + 1}</span>
                      <p className="text-sm font-semibold truncate flex-1">{p.title}</p>
                      {p.video_url && <Badge variant="secondary" className="text-[9px] px-1.5 py-0" data-testid={`top-post-video-badge-${p.id}`}><Film size={9} className="mr-0.5" /> Reel</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground capitalize">{p.platform}{p.brand ? ` · ${p.brand}` : ""} · {p.published_at ? new Date(p.published_at).toLocaleDateString() : ""}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1"><Heart size={12} className="text-destructive" /> {p.real_engagement.likes}</span>
                      <span className="flex items-center gap-1"><MessageCircle size={12} /> {p.real_engagement.comments}</span>
                      {p.real_engagement.shares > 0 && <span className="flex items-center gap-1"><Send size={12} /> {p.real_engagement.shares}</span>}
                      {p.real_engagement.plays != null && <span className="flex items-center gap-1" data-testid={`top-post-plays-${p.id}`}><Play size={12} /> {p.real_engagement.plays} plays</span>}
                      {p.real_engagement.reach != null && <span className="flex items-center gap-1" data-testid={`top-post-reach-${p.id}`}><Eye size={12} /> {p.real_engagement.reach} reach</span>}
                      {p.real_engagement.permalink && <a href={p.real_engagement.permalink} target="_blank" rel="noreferrer" className="ml-auto text-accent hover:underline flex items-center gap-1">View <ArrowRight size={11} /></a>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {funnel.length > 0 && (
        <Card data-testid="signup-funnel-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Signup funnel — link clicks by post
              <Badge variant="secondary" className="ml-2 font-sans font-normal text-[10px]">tracked short links</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {(() => { const max = Math.max(...funnel.map((p) => p.clicks), 1); return funnel.map((p, i) => (
              <div key={p.id} className="flex items-center gap-3" data-testid={`funnel-row-${p.id}`}>
                <span className="text-[10px] font-bold text-accent w-6 text-right">#{i + 1}</span>
                {p.image_url ? <img src={assetUrl(p.image_url)} alt="" className="w-9 h-9 rounded-md object-cover shrink-0" /> : <div className="w-9 h-9 rounded-md bg-muted shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold truncate">{p.title}</p>
                    <span className="text-[10px] text-muted-foreground capitalize shrink-0">{p.platform}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted mt-1 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.max((p.clicks / max) * 100, 2)}%`, background: "hsl(var(--accent-2))" }} />
                  </div>
                </div>
                <div className="text-right shrink-0 w-20">
                  <p className="text-sm font-bold flex items-center justify-end gap-1"><MousePointerClick size={13} className="text-accent" /> {p.clicks}</p>
                  <a href={p.track_url} target="_blank" rel="noreferrer" className="text-[9px] text-muted-foreground hover:text-accent hover:underline">{p.track_url?.replace("https://", "")}</a>
                </div>
              </div>
            )); })()}
          </CardContent>
        </Card>
      )}

      <ReelInsights />
    </div>
  );
}
